import React from 'react';
import { cn } from '~/lib/utils';
import { ActivityGlyph } from './ActivityGlyph';
import styles from './QueueMeta.module.css';
import type { ChatTheme } from './theme';
import { chatThemeClass } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface QueueMetaProps {
  /**
   * The status word. `queued` is waiting its turn; `editing` is the same message with the caret in
   * it. Nothing else about the row changes between the two — the whole state lives in one word,
   * which is the point of the queue: a message you have not sent yet is still just your message.
   */
  status: 'queued' | 'editing';
  /** Drops this message from the queue. The X is drawn on every queued row, one per message. */
  onCancel?: () => void;
  /**
   * Name for the X, rendered as both `aria-label` and `title` so the pointer and the screen reader
   * are told the same thing. Defaults to a phrase that names the message it removes rather than
   * the glyph, because "Close" beside three identical rows tells a screen-reader user nothing.
   */
  cancelLabel?: string;
  theme?: ChatTheme;
  className?: string;
}

const STATUS_LABEL: Record<QueueMetaProps['status'], string> = {
  queued: 'Queued',
  editing: 'Editing',
};

/**
 * The status line under a queued message: one word, and the X that cancels it.
 *
 * Drawn seven times across the four `Queue` artboards — once on *One queued*, three times on
 * *Three queued*, twice on *Editing a queued one* (the second reading `Editing`), once on
 * *Queue drains*. It is a
 * **sibling** of `UserBubble`, hung 4px below it and right-aligned by the row's own
 * `align-items: flex-end`.
 *
 * Per-message, deliberately. The composer states the aggregate ("3 queued") once, in the toolbar;
 * this row states which message and offers the only way to take that one back. The *Three queued*
 * caption is the rule: "each cancellable on its own. the count lives in the composer, once."
 */
export const QueueMeta = React.forwardRef<HTMLDivElement, QueueMetaProps>(function QueueMeta(
  { status, onCancel, cancelLabel = 'Remove this queued message', theme: themeProp, className },
  ref
) {
  const theme = useV15Theme(themeProp);
  return (
    <div ref={ref} className={cn(chatThemeClass(theme), styles.meta, className)}>
      {/* Polite, because this word changes without the user watching it: a run finishing promotes
          the message above and re-flows the queue, and a message can go from `Queued` to `Editing`
          from a keyboard shortcut. It is one word, so announcing it costs nothing. */}
      <span className={styles.label} aria-live="polite">
        {STATUS_LABEL[status]}
      </span>
      {/* `title` alongside `aria-label`, so a pointer user gets the same name a screen-reader user
          gets — the pattern `MessageActions`, `ComposerButton` and the header's `IconSlot` all
          follow. It matters most here: the Three queued artboard stacks three identical X's in
          one column with nothing but position to tell them apart. */}
      <button
        type="button"
        className={styles.cancel}
        aria-label={cancelLabel}
        title={cancelLabel}
        onClick={onCancel}
      >
        {/* `inherit`: the X rests at hint with the "Queued" word beside it and lifts to muted on
            the button's own hover, so the tone is the button's to state, not the mark's. */}
        <ActivityGlyph chrome="close" size={14} tone="inherit" theme={theme} />
      </button>
    </div>
  );
});
